<template>
    <validation-provider tag="div"
                         class="froala-wrapper tw-relative"
                         :rules="rules"
                         :name="name"
                         :vid="vid"
                         v-slot="{ errors, classes }"
                         :class="[
                             {
                                 'empty': currentValue.length === 0,
                                 'active': gif_menu_open || emoji_menu_open,
                                 'no-submit-on-enter': ((!settings.submit_on_enter || settings.submit_on_enter == '0') && submittable),
                                 'prestine': prestine,
                                 'initialized': initialized
                             },
                             type]">
        <!-- image picker -->
        <input ref="huddleImagePicker" class="hidden" type="file" :accept="fileTypes" @change="addImage">
        <div class="image-previews">
            <huddle-editor-images v-for="(preview, index) in imagePreview"
                                  :type="uploadedImages[index].type"
                                  :name="uploadedImages[index].name"
                                  :images="uploadedImages"
                                  :uuids="uploadedImagesUUIDs"
                                  :preview="preview"
                                  :index="index"
                                  :key="uploadedImagesUUIDs[index]"
                                  v-on="$listeners"
                                  @imageUploaded="removeImageFromUploadingList"
                                  @imageRemoved="removeImage">
            </huddle-editor-images>
        </div>

        <!-- editor -->
        <froala :config="froala_config"
                v-model="currentValue"
                ref="froala"
                :class="multi_line ? 'overflow-wrap-anywhere' : ''"
                :onManualControllerReady="saveInitControls">
        </froala>

        <div class="froala-wrapper tw-relative uninitialized" v-if="!initialized" @mouseenter="initialize" @click="initialize(true)">
            <div class="fr-box fr-basic">
                <div class="fr-wrapper show-placeholder">
                    <div class="fr-element">
                        <template v-if="value && value !== '' && value.length > 0">
                            <froalaView v-model="currentValue"></froalaView>
                        </template>
                        <br v-else/>
                    </div>
                    <span class="fr-placeholder tw-cursor-text" tabindex="0" @focus="initialize(true)" @click="initialize(true)" v-text="placeholder" v-if="!value || (value && (value === '' || value.length === 0))"></span>
                </div>
            </div>
        </div>

        <span class="form-validation-error" v-if="errors.length > 0">{{ errors[0] }}.</span>

        <!-- mentions -->
        <div class="tributes-wrapper tw-px-3 tw-py-4" ref="tributes" v-show="tribute_search_results.length > 0">
            <h2 class="tiny-text light-text tw-pb-3 tw-mb-3 tw-border-b-2 tw-border-gray-0">Users for: <b class="normal-text bold-text">@{{ tribute_query }}</b></h2>
        </div>

        <!-- gif preview -->
        <div v-if="display_gif_preview" class="gif-mobile-preview tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0 tw-flex tw-items-center tw-justify-center">
            <div class="gif-preview-wrapper tw-flex" v-click-outside="closeGifPreview">
                <img :src="gif_preview" class="tw-m-auto tw-rounded">
                <div class="tw-absolute tw-flex tw-items-center tw-bottom-0 tw-left-0 tw-w-full tw-justify-between tw-px-8 tw-py-6">
                    <i class="far fa-times tw-text-white tw-text-2xl hover-opacity" @click="closeGifPreview"></i>
                    <div class="gif-preview-submit tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-full tw-text-green-700 hover-opacity"
                         @click="submitGifPreview">
                        <i class="fa fa-paper-plane tw-text-xl"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- emojis -->
        <transition name="toggle-emojis">
            <div v-if="emoji_menu_open"
                 class="emoji-wrapper"
                 v-click-outside="closeEmojiPicker">
                <div class="emoji-switches">
                    <div class="emoji-switch-fade"></div>
                    <i v-for="tab in emoji_tabs"
                       class="far mimic-title"
                       @click="setEmojiTab(tab.name)"
                       :class="[{'secondary-c': active_emoji_tab === tab.name}, 'fa-' + tab.icon]">
                        <span v-text="tab.name"></span>
                    </i>
                </div>
                <div class="tw-overflow-scroll">
                    <div class="tw-flex tw-flex-col tw-mt-2">
                        <div class="emoji-search">
                            <input type="text" class="tw-bg-gray-100" ref="searchEmojisInput" placeholder="Find your emoji.." v-model="emoji_search_input">
                        </div>
                        <div class="emojis">
                        <span v-for="emoji in visible_emojis"
                              class="hover tw-select-none"
                              v-html="'&#x' + emoji.codes.substr(0, 5)"
                              @click="insertIcon(emoji, '&#x' + emoji.codes.substr(0, 5))">
                        </span>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- gifs -->
        <transition name="toggle-emojis">
            <div v-if="gif_menu_open" class="emoji-wrapper gif-picker expanded" v-click-outside="closeGifPicker">
                <!-- search header -->
                <div class="gif-search tw-mt-2 tw-flex tw-items-center">
                    <i class="fa fa-arrow-left tw-ml-2 tw-mb-1 tw-mr-4 tw-text-gray-400 hover-opacity"
                       v-if="gif_results.length > 0 || gif_search_input !== ''"
                       @click="toggleGifPicker(true)"></i>
                    <i class="close-gif-picker fa fa-times tw-ml-2 tw-mb-1 tw-mr-4 tw-text-gray-400 hover-opacity"
                       v-if="gif_results.length === 0 && gif_search_input === ''"
                       @click="toggleGifPicker()"></i>
                    <input type="text" class="tw-bg-gray-100"
                           ref="searchGifsInput"
                           placeholder="Search for GIFS.."
                           v-model="gif_search_input"
                           @keyup="searchGifsByInput">
                </div>

                <div class="emoji-switches tw--mt-2">
                    <div class="emoji-switch-fade"></div>
                </div>

                <div class="emojis tw-overflow-scroll">
                    <div class="gif-wrapper tw-w-full tw-mx-2">
                        <div class="tw-overflow-scroll">
                            <!-- gif categories -->
                            <div class="gif-categories tw-mt-1 grid col-4 gap-x-1 sm-col-3 row-gap-1" v-if="gif_results.length === 0">
                                <template v-if="gif_categories.length > 0">
                                    <div class="tw-bg-black tw-relative tw-rounded tw-overflow-hidden hover-opacity"
                                         v-for="category in gif_categories"
                                         @click="searchGifsByCategory(category.name)"
                                         :key="category.name">
                                        <img class="tw-w-full tw-opacity-75" :src="category.image">
                                        <p class="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-mx-auto tw-text-center tw-font-bold tw-text-white tw-pt-7 tw-text-sm">
                                            {{ category.name }}</p>
                                    </div>
                                </template>
                                <!-- placeholder skeletons for categories -->
                                <template v-else>
                                    <div class="tw-rounded tw-overflow-hidden hover-opacity" v-for="n in 20" :key="n">
                                        <div class="category-placeholder tw-w-full tw-opacity-75"></div>
                                    </div>
                                </template>
                            </div>

                            <!-- gif results -->
                            <div class="gifs tw-w-full tw-mt-1 tw-relative">
                                <div class="tw-w-full">
                                    <masonry :cols="3" :gutter="10">
                                        <div class="tw-rounded tw-overflow-hidden hover-opacity tw-mb-3"
                                             v-for="gif in gif_results"
                                             @click="chooseGif(gif.media[0].gif.url)">
                                            <div class="gif-placeholder tw-relative tw-w-full" :style="{height: (gif.media[0].tinygif.dims[1] / 2) + 'px'}">
                                                <img class="tw-w-full tw-absolute tw-w-full" :src="gif.media[0].tinygif.url">
                                            </div>
                                        </div>
                                    </masonry>
                                </div>
                                <div class="tw-absolute tw-bg-white tw-w-full tw-h-full tw-w-full tw-top-0 tw-left-0"
                                     v-if="!display_gif_results"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </validation-provider>
</template>

<script>
    import { mapMutations } from 'vuex';
    import Tribute from "tributejs";
    import FroalaEditor from 'froala-editor';
    import axios from 'axios';
    import vClickOutside from 'v-click-outside';
    import loadImage from 'blueimp-load-image';

    import Global from '../mixins/Global';
    import Validation from '../mixins/v2/Validation';
    import { EventBus } from '../lib/event-bus'
    import emojis from '../lib/emojis';
    import heic2any from "heic2any";

    FroalaEditor.ICON_DEFAULT_TEMPLATE = 'font_awesome_5r';

    export default {
        name: 'HuddleEditorV2',

        mixins: [Global, Validation],

        props: {
            value: {
                required: true,
            },
            submit_on_enter: {
                default: false,
            },
            prevent_submit: {
                default: false,
            },
            single_line: {
                default: false,
            },
            allow_enter_shift: {
                default: true,
            },
            emojis: {
                default: false,
            },
            fileTypes: {
                default: ".jpg, .png, .jpeg, .gif, .bmp, .heic, .heif, .JPG, .PNG, .JPEG, .GIF, .BMP, .HEIC, .HEIF",
            },
            close_emoji_picker_on_select: {
                default: false,
            },
            gifs: {
                default: false,
            },
            images: {
                default: false,
            },
            mentions: {
                default: false,
            },
            board_id_prop: {
                default: false,
            },
            customData: {
                default: false,
            },
            mobile_send_btn: {
                default: false,
            },
            placeholder: {
                default: '',
            },
            submittable: {
                default: false,
            },
            type: {
                default: 'input',
            },
            init_on_mount: {
                default: false,
            },
            cursor_to_end_on_init: {
                default: false,
            },
            multi_line: {
                default: true,
            },
            rules: {
                type: [String, Object],
                default: '',
            },
            name: {
                type: String,
                default: '',
            },
            vid: {
                type: String,
                default: undefined,
            },
            show_content_alert: {
                type: Boolean,
                default: true,
            },
            disable_spinner: {
                type: Boolean,
                default: false,
            }
        },

        data() {
            const self = this;

            return {
                // editor vars
                initializing: false,
                initialized: false,
                init_controls: false,
                currentValue: '',
                editor_uuid: false,
                editor_active: false,
                editor_selection: {
					start: 0,
					end: 0,
					start_node: 0,
					end_node: 0,
				},
                prestine: true,
                // mentions vars
                tribute: {},
                tribute_query: '',
                tribute_search_results: [],
                // gif vars
                tenor_api_key: 'CKGM5YORX1G6',
                gif_menu_open: false,
                gif_categories: [],
                gif_results: [],
                gif_search_input: '',
                display_gif_results: false,
                cancel_gif_search_token: axios.CancelToken,
                display_gif_preview: false,
                gif_preview: '',
                // image picker vars
                uploadedImages: [],
                imagePreview: [],
                uploadedImagesUUIDs: [],
                imageCount: 0,
                show_upload_preview_box: false,
                currently_uploading_images: [],
                // emoji vars
                emoticons: false,
                emoji_search_input: '',
                emoji_menu_open: false,
                active_emoji_tab: 'Recent',
                emoji_tabs: [
                    {
                        name: 'Recent',
                        icon: 'clock',
                    },
                    {
                        name: 'Smileys & Emotion',
                        icon: 'smile'
                    },
                    {
                        name: 'People & Body',
                        icon: 'child'
                    },
                    {
                        name: 'Animals & Nature',
                        icon: 'paw'
                    },
                    {
                        name: 'Food & Drink',
                        icon: 'mug'
                    },
                    {
                        name: 'Travel & Places',
                        icon: 'mountain'
                    },
                    {
                        name: 'Activities',
                        icon: 'futbol'
                    },
                    {
                        name: 'Objects',
                        icon: 'lightbulb'
                    },
                    {
                        name: 'Symbols',
                        icon: 'icons-alt'
                    },
                    /*
                    {
                        name: 'Flags',
                        icon: 'flag'
                    },
                     */
                ],
                // froala
                froala_config: {
                    key: process.env.VUE_APP_FROALA_KEY,
                    placeholderText: '',
                    tabIndex: 0,
                    attribution: false,
                    // htmlUntouched: true,
                    htmlRemoveTags: ['script', 'style', 'base'],
                    pasteDeniedAttrs: ['style', 'class', 'id', 'data', 'data-.*'],
                    pasteDeniedTags: ['div', 'input', 'base', 'script', 'style', 'aside', 'button', 'head', 'header', 'frame', 'frameset', 'footer', 'form', 'canvas', 'body', 'html', 'label', 'legend', 'link', 'main', 'meta', 'meter', 'nav', 'noframes', 'noscript', 'object', 'optgroup', 'option', 'output', 'param', 'picture', 'progress', 'q', 'source', 'svg', 'template', 'textarea', 'time', 'title', 'var', 'video', 'wbr', 'address', 'area', 'article', 'audio', 'iframe', 'pre'],
                    linkEditButtons: [],
                    pastePlain: true,
                    charCounterCount: false,
                    quickInsertEnabled: false,
                    toolbarButtons: [],
                    fileAllowedTypes: ['application/pdf', 'application/msword'],
                    emoticonsUseImage: false,
                    multiLine:this.multi_line,
                    enter: FroalaEditor.ENTER_BR,
					shortcutsEnabled: ['show', 'indent', 'outdent', 'redo', 'undo'],
                    events: {
                        'initialized': function onInit() {
                            self.initialized = true;
                        },
                        'focus': function showToolbar() {
                            self.prestine = false;
                            self.$emit('focus');

                            // display editor
                            self.editor.$box[0].classList.add('active');
                            self.editor.$box[0].classList.remove('idle');

                            // set editor active
                            self.editor_active = true;
                            self.setActiveEditorUUID(self.editor_uuid);

                            // load emojis
                            self.setEmoticonList();
                        },
                        'blur': function blur() {
							// close the editor
                            self.editor.$box[0].classList.remove('active');
                            self.editor.$box[0].classList.add('idle');
                            self.editor_active = false;
                        },
                        'keydown': function (keydownEvent) {
                            self.$emit('onkeydown');
                        },
                        'keyup': function (keydownEvent) {
                            self.$emit('onkeyup');

							// save the current selection for later use
							self.saveSelection();
                        },
						'mouseup': function (mouseupEvent) {
							// save the current selection for later use
							self.saveSelection();
						},
                    },
                    immediateVueModelUpdate: true, // this is needed when using initControls.getEditor() in initialize else the v-model wont work for some reason
                },
            };
        },

        computed: {
            visible_emojis() {
                if (this.emoji_search_input === '') {
                    if (this.active_emoji_tab === 'Recent') {
                        const recent_emojis = window.localStorage.getItem('recentEmojis');

                        if (recent_emojis !== null) {
                            return JSON.parse(recent_emojis);
                        }

                        return [];
                    } else {
                        return this.emoticons.filter(emoji => (emoji.category === this.active_emoji_tab));
                    }
                } else {
                    return this.emoticons.filter(emoji => (emoji.keywords.indexOf(this.emoji_search_input) !== -1));
                }
            },
        },

        methods: {
            ...mapMutations(['setActiveEditorUUID']),

            saveInitControls(initControls) {
                this.init_controls = initControls;
            },

            initialize(focus = false, force_init = false, move_cursor_to_end = false) {
                // if the editor hasn't been initialized yet and translations have been loaded
              if (!this.initializing && !this.initialized && (Object.keys(this.translations).length !== 0 || force_init)) {
                    this.initializing = true;

                    const self = this;

                    this.editor_uuid = this.generateUUID();

                    this.setupMentions();
                    this.setupButtonListeners();

                    // set placeholder text
                    this.froala_config.placeholderText = this.placeholder;

                    // initalize the editor
                    this.init_controls.initialize(true);

                    // save the editor so we can use it later
                    this.editor = this.init_controls.getEditor();

                    // bind events to editor
                    setTimeout(() => {
                        if (this.mentions) {
                            this.tribute.attach(this.editor.$el[0]);

                            this.editor.$el[0].addEventListener('tribute-active-false', function () {
                                self.tribute_search_results = [];
                            });
                        }

                        this.editor.events.on('keydown', function (e) {
                            if (e.which === FroalaEditor.KEYCODE.ENTER) {
                                // on mention
                                if (self.tribute.isActive) {
                                    return false;
                                    // on enter + shift key
                                } else if(e.shiftKey) {
                                    if (self.single_line && !self.allow_enter_shift) {
                                        e.stopImmediatePropagation();
                                        e.preventDefault();
                                        return false;
                                    }
                                } else {
                                    // on normal enter
                                    if ((self.submit_on_enter && self.settings.submit_on_enter != 0) || self.single_line) {
                                        // prevent new line
                                        e.stopImmediatePropagation();
                                        e.preventDefault();

                                        // if (!self.single_line) {
                                            self.submit();
                                        // }

                                        return false;
                                    }
                                }
                            }
                        }.bind(this), true);
                    }, 0);

                    if (focus === true) {
                        setTimeout(() => {
                            self.editor.events.focus();

                            if (move_cursor_to_end === true) {
                                self.editor.selection.setAtEnd(self.editor.$el.get(0));
                                self.editor.selection.restore();
                            }
                        }, 0);
                    }
                }
            },

            submit() {
                if(this.prevent_submit) return;
                const content = this.editor.html.get();

                if (content.length > 0) {
                    // toggle spinner

                    if(!this.disable_spinner){
                        this.$store.commit('toggleSpinner', true);
                    }

                    this.editor.html.cleanEmptyTags();

                    // emit submit
                    this.$emit('submit', {
                        content: this.editor.html.get(),
                        custom_data: this.customData,
                        images: this.uploadedImages,
                        imagesPreview: this.imagePreview,
                        imagesUUIDs: this.uploadedImagesUUIDs,
                    });

                    // reset editor
                    this.clearEditor();
                } else {
                    this.show_content_alert && alert('Content is required to post!');
                }
            },

            searchUsersByTag(query, callback) {
                if (query.length > 1) {
                    const params = new URLSearchParams();

                    params.append('query', query);
                    params.append('model', 'users');
                    params.append('channel_id', this.board_id_prop);

                    const request = {
                        params,
                    };

                    axios.get('/api/v3/search', request).then((response) => {
                        const users = response.data.results.users.data;

                        this.tribute_search_results = users;

                        callback(users);
                    });
                }
            },

            closeGifPicker() {
                this.gif_search_input = '';
                this.gif_menu_open = false;
            },

            toggleGifPicker(force_open = false) {
                this.emoji_menu_open = false;
                this.gif_search_input = '';

                if (!force_open) {
                    this.gif_menu_open = !this.gif_menu_open;
                }

                //grab trending gif categories
                if (this.gif_menu_open) {
                    let cat_url = "https://api.tenor.com/v1/categories?key=" + this.tenor_api_key;

                    this.getGifsByUrl(cat_url, this.setGifCategories);

                    this.$nextTick(() => {
                        this.$refs.searchGifsInput.focus();
                    });
                }
            },

            //grab gifs by clicked category
            searchGifsByCategory(category) {
                let url = "https://api.tenor.com/v1/search";

                let request = {
                    tag: category,
                    key: this.tenor_api_key,
                    limit: 30
                };

                this.getGifsByUrl(url, this.setGifs, request);
            },

            //grab gifs by input from user
            searchGifsByInput() {
                let source = this.cancel_gif_search_token.source();
                let url = "https://api.tenor.com/v1/search";

                let request = {
                    tag: this.gif_search_input,
                    key: this.tenor_api_key,
                    limit: 30
                };

                source.cancel();

                this.getGifsByUrl(url, this.setGifs, request);
            },

            //make the search call to the tenor api
            getGifsByUrl(theUrl, callback, options = false) {
                let source = this.cancel_gif_search_token.source();
                let axios_instance = axios.create();

                axios_instance.get(theUrl, {
                    params: options,
                    cancelToken: source.token,
                    transformRequest: [(data, headers) => {
                        delete headers.common['X-CSRF-TOKEN'];
                        delete headers.common['X-Requested-With'];
                        return data
                    }]
                }).then((response) => {
                    callback(response.data);
                });
            },

            //display gif categories
            setGifCategories(response) {
                this.gif_results = [];
                this.gif_categories = response.tags;
            },

            //display gif results
            setGifs(response) {
                this.gif_results = [];
                this.gif_results = response.results;
                this.display_gif_results = true;
            },

            chooseGif(gif_url) {
                //on desktop, submit image
                if (window.innerWidth > 767) {
                    this.submitGif(gif_url);
                } else {
                    //on mobile, preview image first
                    this.display_gif_preview = true;
                    this.gif_preview = gif_url;
                }
            },

            closeGifPreview() {
                if (this.gif_preview) {
                    this.display_gif_preview = false;
                    this.gif_preview = '';
                }
            },

            submitGifPreview() {
                this.submitGif(this.gif_preview);
                this.closeGifPreview();
            },

            submitGif(gif_url) {
                this.gif_menu_open = false;
                this.currentValue = '';

                // toggle spinner
                this.$store.commit('toggleSpinner', true);

                // emit gif
                this.$emit('submit', { gif: gif_url, custom_data: this.customData });

                // reset editor
                this.clearEditor();
            },

            toggleEmojiPicker() {
                this.gif_menu_open = false;

                if (this.emoji_menu_open === false) {
                    this.emoji_menu_open = true;
                    const recent_emojis = window.localStorage.getItem('recentEmojis');

                    if (recent_emojis === null) {
                        this.active_emoji_tab = 'Smileys & Emotion';
                    }

                    this.$nextTick(() => {
                        this.$refs.searchEmojisInput.focus();
                    });
                } else {
                    this.closeEmojiPicker();
                }
            },

            closeEmojiPicker(mouse_event) {
                this.emoji_menu_open = false;

                // if not clicked outside
                if (typeof mouse_event === 'undefined') {
                    this.restoreSelection();
                }
            },

            setEmojiTab(tab) {
                this.active_emoji_tab = tab;
            },

            setEmoticonList() {
                if (!this.emoticons) {
                    this.emoticons = emojis;
                }
            },

            //on select icon
            insertIcon(emoji, icon) {
                this.restoreSelection();
                this.editor.html.insert(icon);

                let emojis = [];
                const recent_emojis = window.localStorage.getItem('recentEmojis');

                if (recent_emojis !== null) {
                    emojis = JSON.parse(recent_emojis);
                }

                const used_emoji_index = emojis.findIndex(used_emoji => used_emoji.codes === emoji.codes);

                if (used_emoji_index !== -1) {
                    emojis.splice(used_emoji_index, 1);
                }

                emojis.unshift(emoji);

                window.localStorage.setItem('recentEmojis', JSON.stringify(emojis));

                if (this.close_emoji_picker_on_select) {
                    this.closeEmojiPicker();
                }
            },

			// save the current offset and node for later use
			saveSelection() {
				// get the selection from froala
				const selection = this.editor.selection.get();

				// current position of the caret
				this.editor_selection.start = selection.anchorOffset;
				// end position of the caret, usually the same as start unless highlighted
				this.editor_selection.end = selection.focusOffset;
				// starting node (line)
				this.editor_selection.start_node = selection.anchorNode;
				// ending line
				this.editor_selection.end_node = selection.focusNode;
			},

			// restore the caret to the previously remembered position
            restoreSelection() {
				// focus the editor
				this.editor.$el[0].focus();

				// create range and selection objects
				const range = document.createRange();
				const sel = window.getSelection();

				// prepare range for proper positions
				range.setStart(this.editor_selection.start_node, this.editor_selection.start);
				range.setEnd(this.editor_selection.end_node, this.editor_selection.end);

				// remove current ranges
				sel.removeAllRanges();

				// add the newly created range
				sel.addRange(range);
            },

            // toggle escape listener for emoji/gif modals
            toggleEscapeListener(val) {
                if (val === true) {
                    document.addEventListener('keydown', this.closeOnEscape);

                    this.$once('hook:destroyed', () => {
                        document.removeEventListener('keydown', this.closeOnEscape);
                    });
                } else {
                    document.removeEventListener('keydown', this.closeOnEscape);
                }
            },

            // close emoji/gif modals on escape
            closeOnEscape(e) {
                if (e.keyCode === 27) {
                    this.closeGifPicker();
                    this.closeEmojiPicker();
                    this.restoreSelection();
                }
            },

            openImageUploader() {
                if (this.imageCount < 5) {
                    this.$refs.huddleImagePicker.click();
                }
            },

            async addImage(event) {
                let file = event.target.files[0];
                const imageFileTypes = ['jpg', 'heic', 'jpeg', 'png', 'gif', 'bmp', 'heif'];

                if (["image/heic", "image/heif"].includes(file.type)) {
                    const pngFromHeic = await heic2any({ blob: file, toType: "image/png" });
                    file = new File([pngFromHeic], `${file.name.substring(0, file.name.lastIndexOf("."))}.png`, { type: "image/png" });
                }

                const fileExt = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length) || file.name;

                let self = this;

                //generate the uuid that we will use to link the topic to this upload
                let uuid = this.generateUUID();

                //add it's uuid to the currently uploading list
                this.currently_uploading_images.push(uuid);

                //display the preview box so we immediately see a box even while image rendering is going on
                this.show_upload_preview_box = true;

                //wait for preview box to render
                this.$nextTick(() => {
                    this.uploadedImages.push(file);
                    this.uploadedImagesUUIDs.push(uuid);

                    if (imageFileTypes.includes(fileExt.toLowerCase())) {
                        loadImage.parseMetaData(file, function(data) {
                            //default image orientation
                            let orientation = 0;

                            //if exif data available, update orientation
                            if (data.exif) {
                                orientation = data.exif.get('Orientation');
                            }

                            loadImage(
                                file,
                                function(canvas) {
                                    //add the parsed image with correct orientation to the previews
                                    self.imagePreview.push(canvas.toDataURL('image/jpeg'));
                                    self.show_upload_preview_box = false;
                                }, {
                                    //should be set to canvas : true to activate auto fix orientation
                                    canvas: true,
                                    orientation: orientation
                                }
                            );
                        });
                    } else {
                        // file is not an image, show alternative image for image preview
                        if (fileExt === 'pdf' || fileExt === 'doc' || fileExt === 'docx') {
                            self.imagePreview.push('');
                            self.show_upload_preview_box = false;
                        }
                    }
                    this.imageCount++;
                });
            },

            removeImageFromUploadingList(uuid) {
                //remove this images' uuid from the currently uploading list
                this.currently_uploading_images.splice(this.currently_uploading_images.indexOf(uuid), 1);
            },

            removeImage(payload) {
                this.$refs.huddleImagePicker.value = '';

                //delete from uploads
                this.$store.dispatch('removeImage', payload.uuid).then(() => {
                    //remove the image from previews
                    this.imagePreview.splice(payload.index, 1);
                    this.uploadedImages.splice(payload.index, 1);
                    this.uploadedImagesUUIDs.splice(payload.index, 1);

                    this.imageCount--;
                });
            },

            clearEditor() {
                this.currentValue = '';
                this.uploadedImages = [];
                this.imagePreview = [];
                this.uploadedImagesUUIDs = [];
                this.imageCount = 0;
                this.gif_menu_open = false;
                this.emoji_menu_open = false;

                this.editor.edit.off();
                this.editor.edit.on();
                this.$refs.huddleImagePicker.value = '';
            },

            setupMentions() {
                const self = this;

                // setup tagging system
                if (this.mentions) {
                    this.tribute = new Tribute({
                        values: function (value, cb) {
                            self.tribute_query = value;

                            self.searchUsersByTag(value, users => cb(users));

							self.saveSelection();

                            self.closeGifPicker();
                            self.closeEmojiPicker();
                        },
                        // template for displaying item in menu
                        menuItemTemplate: function (item) {
                            return `<div>
                                    <div class="profile-icon-s tw-mr-3">
                                        <img src="${item.original.avatar}">
                                    </div>
                                    ${item.original.firstname} ${item.original.lastname}
                                </div>
                                <span class="light-text tiny-text tw-ml-2">
                                    ${item.original.username}
                                </span>`
                        },
                        selectTemplate: function(item) {
                            self.tribute_search_results = [];
                            return `<a href=${window.location.origin}/profile/${item.original.username}"><span class="fr-deletable fr-tribute bold" data-mention="${item.original.id}">${item.original.username}</span></a>`;
                        },
                        lookup: function (person) {
                            return person.username + person.firstname + person.lastname;
                        },
                        menuContainer: this.$refs.tributes,
                        selectClass: 'tw-bg-gray-0',
                        itemClass: 'tw-flex tw-items-center normal-text tw-justify-between tw-p-3 tw-rounded-lg tw-cursor-pointer hover:tw-opacity-75'
                    });
                }
            },

            setupButtonListeners() {
                // setup gifs listener
                if (this.gifs) {
                    this.froala_config.toolbarButtons.push('gifs');

                    EventBus.$on(`gif-btn-clicked-${this.editor_uuid}`, () => {
                        this.toggleGifPicker();
                    });
                }

                // setup emojis listener
                if (this.emojis) {
                    this.froala_config.toolbarButtons.push('emojis');

                    EventBus.$on(`emoji-btn-clicked-${this.editor_uuid}`, () => {
                        this.toggleEmojiPicker();
                    });
                }

                // setup images listener
                if (this.images) {
                    this.froala_config.toolbarButtons.push('images');

                    EventBus.$on(`image-btn-clicked-${this.editor_uuid}`, () => {
                        this.openImageUploader();
                    });
                }

                // setup images listener
                if ((this.mobile_send_btn || !this.settings.submit_on_enter) && this.submittable) {
                    this.froala_config.toolbarButtons.push('submit');

                    EventBus.$on(`submit-btn-clicked-${this.editor_uuid}`, () => {
                        this.submit();
                    });
                }
            },
        },

        created() {
            this.currentValue = this.value;
        },

        mounted() {
            if (this.init_on_mount) {
                const cursor_to_end = this.cursor_to_end_on_init;

                this.initialize(true, true, cursor_to_end);
            }
        },

        watch: {
            currentValue(val) {
                this.$emit('input', val);
            },

            value(val) {
                this.currentValue = val;
            },

            emoji_menu_open(val) {
                this.toggleEscapeListener(val);
            },

            gif_menu_open(val) {
                this.toggleEscapeListener(val);
            },
        },

        directives: {
            clickOutside: vClickOutside.directive,
        }
    };
</script>

<style lang="scss">
    .overflow-wrap-anywhere {
        .fr-element {
            overflow-wrap: anywhere;
        }
    }

    .froala-wrapper {
        .image-previews {
            position: absolute;
            right: 0;
            bottom: calc(100% + 8px);
            border-radius: 3px;
            opacity: 0.6;
            display: flex;

            .image-preview {
                margin-left: 10px;
                display: flex;
                align-items: flex-end;
                width: 140px;
                border-radius: 3px;
                background: rgba(0, 0, 0, 0.3);

                img, p {
                    border-radius: 3px;
                    background: rgba(0, 0, 0, 0.3);
                    transition: border 0.3s ease-in-out;
                    border: 2px solid transparent;

                    &:hover {
                        cursor: pointer;
                        border: 2px solid #ff9999;
                    }
                }

                img {
                    padding: 5px;
                }

                p {
                    color: #fff;
                    padding: 5px 10px 5px 10px;
                }
            }
        }

        .tributes-wrapper {
            position: absolute;
            z-index: 10;
            display: block;
            left: 0;
            width: 100%;
            background: #ffffff;
            min-height: 40px;
            bottom: calc(100% + 10px);
            border-radius: 5px;
            box-shadow: 0 3px 4px 4px rgba(0, 0, 0, 0.04);

            .tribute-container {
                top: auto !important;
                bottom: auto !important;
                left: auto !important;
                position: relative !important;
            }
        }

        .gif-mobile-preview {
            z-index: 9998;
            background: rgba(0, 0, 0, .8);

            .gif-preview-wrapper {
                height: fit-content;
                width: fit-content;

                img {
                    max-height: 90vh !important;
                    width: auto !important;
                    max-width: 90vw !important;
                }

                .gif-preview-submit {
                    width: 50px;
                    height: 50px;
                }
            }
        }

        .emoji-wrapper {
            position: absolute;
            z-index: 2;
            border: 0;
            box-shadow: 0 3px 4px 4px rgba(0, 0, 0, 0.04);
            width: 510px;
            background: #fff;
            border-radius: 10px;
            right: 0;
            bottom: 60px;
            display: flex;
            flex-direction: column;
            height: 200px;
            transition: height 0.2s ease;

            &.expanded {
                height: 250px;
            }

            .emoji-switches {
                display: flex;
                align-items: center;
                padding: 8px;
                padding-bottom: 0;
                position: relative;
                z-index: 1;

                .emoji-switch-fade {
                    position: absolute;
                    height: 9px;
                    width: 100%;
                    bottom: -9px;
                    background-image: linear-gradient(to bottom, white, rgba(255, 255, 255, 0));
                    left: 0;
                }

                i {
                    padding: 9px;
                    color: #d4d4d4;
                    flex-basis: 33px;
                    height: 33px;

                    &:hover {
                        cursor: pointer;
                        opacity: 0.8;
                    }
                }
            }

            .emoji-search {
                margin-left: 13px;
                margin-right: 13px;

                input {
                    border: none;

                    &::placeholder {
                        @apply tw-text-gray-500;
                    }
                }
            }

            .emojis {
                display: flex;
                flex-wrap: wrap;
                flex: 1;
                padding: 8px;
                padding-top: 0;

                .gif-wrapper {
                    .gif-placeholder {
                        background: #cbcbcb;
                    }

                    .gif-categories {
                        img, .category-placeholder {
                            height: 70px;
                            background: #cbcbcb;
                        }
                    }
                }

                span {
                    transition: transform 0.25s ease-in-out, -webkit-transform 0.25s ease-in-out;
                    padding: 7px;
                    font-size: 22px;
                    flex-basis: 38px;
                    height: 45px;

                    &:hover {
                        transform: scale(1.5);
                    }
                }
            }

            .gif-search {
                margin-left: 15px;
                margin-right: 15px;
                margin-top: 15px;

                .close-gif-picker {
                    display: none;

                    @media(max-width: 767px) {
                        display: block;
                    }
                }

                input {
                    border: none;

                    &::placeholder {
                        @apply tw-text-gray-500;
                    }
                }
            }

            &.second-button {
                right: 12px;
            }

            &.third-button {
                right: 46px;
            }

            &.gif-picker {
                &.second-button {
                    right: 17px;
                }

                @media(max-width: 767px) {
                    width: 100vw;
                    height: 275px;
                    bottom: 0;
                    position: fixed;
                    z-index: 9997;
                    left: 0;
                    border-radius: 10px 10px 0 0;
                }
            }

            @media(max-width: 576px) {
                width: 373px;
            }

            @media(max-width: 450px) {
                width: 274px;
            }
        }
    }
</style>
